import { FootballGame } from '../types/games_api_types';
import React from 'react';
import { TEAMS } from '../teams';

function ballPosition(game: FootballGame): number | null {
  if (game.ball_yardline == null || game.ball_field_side == null) {
    return null;
  }

  return game.ball_field_side === 'away'
    ? 10 + game.ball_yardline
    : 110 - game.ball_yardline;
}

function ordinal(num?: number) {
  if (num == null) return '';

  const tens = num % 100;
  const units = num % 10;

  if (tens >= 11 && tens <= 13) {
    return `${num}th`;
  }

  switch (units) {
    case 1:
      return `${num}st`;
    case 2:
      return `${num}nd`;
    case 3:
      return `${num}rd`;
    default:
      return `${num}th`;
  }
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

export function FootballField({ game }: { game: FootballGame }) {
  const ballX = ballPosition(game);

  const showDown =
    game.possession !== null && game.down !== null && game.distance !== null;

  let downX: number | null = null;
  let firstDownX: number | null = null;

  if (ballX != null && game.possession) {
    downX = clamp(ballX + (game.possession === 'away' ? 0 : -20), 10, 90);
  }

  if (showDown && ballX !== null && game.distance !== null) {
    firstDownX =
      ballX + (game.possession === 'away' ? game.distance : -1 * game.distance);
  }

  return (
    <svg viewBox="0 0 120 30" width="100%">
      <rect x="0" y="0" width="120" height="30" fill="green" />

      <rect
        x="0"
        y="0"
        width="10"
        height="30"
        fill={`#${TEAMS[game.away_team].endzone_hex_color}`}
      />

      <rect
        x="110"
        y="0"
        width="10"
        height="30"
        fill={`#${TEAMS[game.home_team].endzone_hex_color}`}
      />

      {Array.from({ length: 11 }).map((_, i) => (
        <line
          key={i}
          x1={(i + 1) * 10}
          y1="0"
          x2={(i + 1) * 10}
          y2="30"
          stroke="white"
          strokeWidth="0.25"
        />
      ))}

      <text
        x="60.25"
        y="7"
        fill="white"
        fontSize="4"
        letterSpacing={0.75}
        textAnchor="middle"
        fontFamily={'ForzaBold'}
      >
        50
      </text>

      <text
        x="30.25"
        y="7"
        fill="white"
        fontSize="4"
        letterSpacing={0.75}
        textAnchor="middle"
        fontFamily={'ForzaBold'}
      >
        20
      </text>

      <text
        x="90.25"
        y="7"
        fill="white"
        fontSize="4"
        letterSpacing={0.75}
        textAnchor="middle"
        fontFamily={'ForzaBold'}
      >
        20
      </text>

      <text
        x="6.5"
        y="15"
        fill="white"
        fontSize="5"
        textAnchor="middle"
        transform="rotate(-90 6.5 15)"
      >
        {TEAMS[game.away_team].endzone_abbreviation}
      </text>

      <text
        x="113.5"
        y="15"
        fill="white"
        fontSize="5"
        textAnchor="middle"
        transform="rotate(90 113.5 15)"
      >
        {TEAMS[game.home_team].endzone_abbreviation}
      </text>

      {ballX !== null && firstDownX !== null && (
        <rect
          x={Math.min(ballX, firstDownX)}
          y={0}
          width={Math.abs(ballX - firstDownX)}
          height={30}
          fill="#ebe70e"
          opacity={0.5}
        />
      )}

      {/* Ball marker */}
      {ballX !== null && (
        <line
          x1={ballX}
          y1="0"
          x2={ballX}
          y2="30"
          stroke="#2044e3"
          strokeWidth="0.75"
        />
      )}

      {firstDownX !== null && (
        <line
          x1={firstDownX}
          y1="0"
          x2={firstDownX}
          y2="30"
          stroke="#ebe70e"
          strokeWidth="0.75"
        />
      )}

      {/* Down tag */}
      {showDown && game.down !== null && downX != null && (
        <>
          <rect x={downX} y={22} width={20} height={5} fill="#2044e3" />

          <text
            x={downX + 10}
            y={25.5}
            fill="white"
            fontSize="3"
            textAnchor="middle"
            fontFamily={'ForzaBold'}
          >
            {ordinal(game.down)} & {game.distance}
          </text>

          {game.possession === 'home' ? (
            <polygon
              points={`
                  ${downX},22
                  ${downX - 3},24.5
                  ${downX},27
                `}
              fill="#2044e3"
            />
          ) : (
            <polygon
              points={`
                  ${downX + 20},22
                  ${downX + 23},24.5
                  ${downX + 20},27
                `}
              fill="#2044e3"
            />
          )}
        </>
      )}
    </svg>
  );
}
